import {
  Box,
  Container,
  Divider,
  IconButton,
  InputLabel,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useDispatch, useSelector } from 'react-redux';
import DateDropZone from '../DropZones/DateDropZone';
import DatePicker from '../../DatePicker/DatePicker';
import {
  addSource,
  resetSource,
  setSource,
  deleteSourceFromFlowWithoutIndexing,
} from '../../../redux/Calculations/CalculationsActions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fontSize } from '../../..';
import { useEffect, useMemo, useState } from 'react';
import { RootState } from '../../../redux';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import { CanvasSelect } from '../common/CanvasSelect';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
interface IDateDifference {
  flow: string;
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}
type DifferenceUnitOption = {
  value: 'day' | 'week' | 'month' | 'year';
  label: string;
};

const DateDifference = ({ flow, propKey, isFlowUsedInOtherFlowsAsSource }: IDateDifference) => {
  const { calculations, tablesForSelectedDataSets, chartProperties } = useSelector(
    (state: RootState) => {
      return {
        calculations: state.calculations,
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
        chartProperties: state.chartProperties,
      };
    },
  );
  const dispatch = useDispatch();
  const currCalculation = calculations.properties[propKey];
  const differenceUnits: DifferenceUnitOption[] = [
    {
      value: 'day',
      label: 'Day',
    },
    {
      value: 'week',
      label: 'Week',
    },
    {
      value: 'month',
      label: 'Month',
    },
    {
      value: 'year',
      label: 'Year',
    },
  ];
  const valueTypes = ['text', 'decimal', 'integer', 'boolean', 'date'];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElTo, setAnchorElTo] = useState<null | HTMLElement>(null);
  const currentCalculationSession = currCalculation.currentCalculationSession;

  const activeFlow = currentCalculationSession ? currentCalculationSession.activeFlow : null;
  const calculationInfo = currentCalculationSession
    ? currentCalculationSession.calculationInfo
    : null;
  const source =
    activeFlow && calculationInfo ? calculationInfo.flows[activeFlow][0]?.source : null;

  const sourceType =
    activeFlow && calculationInfo ? calculationInfo.flows[activeFlow][0].sourceType : null;

  const getName = (idx: number): string | null => {
    if (!sourceType || !source || !calculationInfo) return null;
    if (sourceType[idx] === 'field') return calculationInfo.fields[source[idx]].displayName;
    if (sourceType[idx] === 'flow')
      return currCalculation.currentCalculationSession!.flowDisplayNames[source[idx]];
    return source[idx];
  };
  const handleDelete = (propKey: string, activeFlow: string, sourceIdx: number) => {
    if (!source) return;
    /**
     * source length===2  means 1 field/custom date and one  dropdown selected value
     */
    if (source.length === 2) dispatch(resetSource(propKey, activeFlow, 0));
    else
      dispatch(
        deleteSourceFromFlowWithoutIndexing(activeFlow, source[sourceIdx], propKey, sourceIdx),
      );
  };

  const selectedDifferenceUnit = source
    ? (differenceUnits?.find((u) => u.value === source[2]) ?? differenceUnits[0])
    : null;
  const [isShowWarning, setIsShowWarning] = useState<[boolean, boolean]>([false, false]);

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  useEffect(() => {
    if (!sourceType || !source) return;
    const checkWarning = async (idx: number) => {
      if (sourceType[idx] !== 'field') return false;
      try {
        const field = calculationInfo?.fields[source[idx]];
        if (!field) return false;
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        return res.isMissing;
      } catch (error) {
        console.error('Error checking missing column:', error);
        return true;
      }
    };

    (async () => {
      const results = await Promise.all([checkWarning(0), checkWarning(1)]);
      setIsShowWarning([results[0], results[1]]);
    })();
    // eslint-disable-next-line
  }, [source]);

  if (
    !currentCalculationSession ||
    !currentCalculationSession.calculationInfo ||
    !source ||
    !sourceType ||
    !activeFlow
  )
    return null;

  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // paddingInline: "0.5rem",
        margin: 0,
      }}
      maxWidth="xs"
    >
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily:'Axiforma Black',
              fontSize: fontSize.large,
              paddingLeft: '8px',
            }}
          >
            Date Difference
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily:'Axiforma Black',
              fontSize: fontSize.large,
              paddingLeft: '8px',
            }}
          >
            From
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source && source[0] !== null && source[0] !== undefined ? 1 : 0} of 1/1)
            {/* ({source?.length-1<0?0:source.length-1} of 1/1) */}
          </Typography>
        </Box>
        <Tooltip
          title="Add Custom Date"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
            disabled={isFlowUsedInOtherFlowsAsSource}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          {valueTypes.map((type, index) => {
            return (
              <MenuItem
                key={index}
                sx={{ width: '7rem' }}
                disabled={type !== 'date'}
                value={type}
                onClick={() => {
                  setAnchorEl(null);

                  if (source[0] !== null) {
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        new Date().toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                  } else if (source[0] === null && source[2] !== null)
                    dispatch(
                      addSource(
                        propKey,
                        activeFlow,
                        0,
                        new Date().toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                  else {
                    dispatch(
                      addSource(
                        propKey,
                        activeFlow,
                        0,
                        new Date().toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                    // addSource(propKey, activeFlow, 0, "day", "text", 2);
                  }
                }}
              >
                {type.replace(/\b\w/g, (char) => char.toUpperCase())}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
      <DateDropZone
        flow={flow}
        propKey={propKey}
        sourceLength={source.length}
        identifier="from"
        disableDrop={isFlowUsedInOtherFlowsAsSource}
        sx={{ minHeight: '5rem', flexGrow: 0 }}
      >
        {!source[0] ? (
          <p
            style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px', textAlign: 'center' }}
          >
            Drop a column or click +<br /> to add static values
          </p>
        ) : (
          <>
            <ListItem
              className="axisFilterField"
              sx={{
                borderRadius: '5px',
                border: isShowWarning[0] ? '1px solid #850000ff !important' : '',
                marginBlock: '5px',
                marginLeft: '0.5rem',
                paddingLeft: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '24px',
                width: '95%',
                gap: '0.2rem',
              }}
            >
              {!isFlowUsedInOtherFlowsAsSource && (
                <button
                  type="button"
                  className="buttonCommon columnClose"
                  onClick={() => {
                    handleDelete(propKey, activeFlow, 0);
                  }}
                  title="Remove field"
                >
                  <CloseRoundedIcon style={{ fontSize: '13px' }} />
                </button>
              )}
              {isFlowUsedInOtherFlowsAsSource && (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              )}
              {sourceType[0] === 'date' ? (
                <DatePicker
                  value={source[0]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        newValue.toISOString().split('T')[0],
                        'date',
                        0,
                      ),
                    );
                  }}
                  disabled={isFlowUsedInOtherFlowsAsSource}
                />
              ) : sourceType[0] === 'field' || sourceType[0] === 'flow' ? (
                <ListItemText
                  primary={getName(0)}
                  sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
                />
              ) : null}
            </ListItem>
          </>
        )}
      </DateDropZone>
      <Divider />
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily:'Axiforma Black',
              fontSize: fontSize.large,
              paddingLeft: '8px',
            }}
          >
            To
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source && source[1] !== null && source[1] !== undefined ? 1 : 0} of 1/1)
          </Typography>
        </Box>
        <Tooltip
          title="Add Custom Date"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              setAnchorElTo(e.currentTarget);
            }}
            disabled={isFlowUsedInOtherFlowsAsSource}
          >
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu open={Boolean(anchorElTo)} anchorEl={anchorElTo} onClose={() => setAnchorElTo(null)}>
          {valueTypes.map((type, index) => {
            return (
              <MenuItem
                key={index}
                sx={{ width: '7rem' }}
                disabled={type !== 'date'}
                value={type}
                onClick={() => {
                  setAnchorElTo(null);

                  if (source.length === 0) {
                    dispatch(setSource(propKey, activeFlow, 0, null, 'date', 0));
                  }
                  if (source[1] !== null)
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        new Date().toISOString().split('T')[0],
                        'date',
                        1,
                      ),
                    );
                  else
                    dispatch(
                      addSource(
                        propKey,
                        activeFlow,
                        0,
                        new Date().toISOString().split('T')[0],
                        'date',
                        1,
                      ),
                    );
                }}
              >
                {type.replace(/\b\w/g, (char) => char.toUpperCase())}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
      <DateDropZone
        flow={flow}
        propKey={propKey}
        sourceLength={source.length}
        identifier="to"
        disableDrop={isFlowUsedInOtherFlowsAsSource}
        sx={
          source[1] || source[0]
            ? {
                flexGrow: 0,
                height: '5rem',
              }
            : {}
        }
      >
        {!['flow', 'field', 'date'].includes(sourceType[1]) ? (
          <p
            style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px', textAlign: 'center' }}
          >
            Drop a column or click +<br /> to add static values
          </p>
        ) : (
          <>
            <ListItem
              className="axisFilterField"
              sx={{
                borderRadius: '5px',
                border: isShowWarning[1] ? '1px solid #850000ff !important' : '',
                marginBlock: '5px',
                marginLeft: '0.5rem',
                paddingLeft: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '24px',
                width: '95%',
                gap: '0.2rem',
              }}
            >
              {!isFlowUsedInOtherFlowsAsSource && (
                <button
                  type="button"
                  className="buttonCommon columnClose"
                  onClick={() => {
                    handleDelete(propKey, activeFlow, 1);
                  }}
                  title="Remove field"
                >
                  <CloseRoundedIcon style={{ fontSize: '13px' }} />
                </button>
              )}
              {isFlowUsedInOtherFlowsAsSource && (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              )}
              {sourceType[1] === 'date' ? (
                <DatePicker
                  value={source[1]}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    dispatch(
                      setSource(
                        propKey,
                        activeFlow,
                        0,
                        newValue.toISOString().split('T')[0],
                        'date',
                        1,
                      ),
                    );
                  }}
                  disabled={isFlowUsedInOtherFlowsAsSource}
                />
              ) : sourceType[1] === 'field' || sourceType[1] === 'flow' ? (
                <ListItemText
                  primary={getName(1)}
                  sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
                />
              ) : null}
            </ListItem>
          </>
        )}
      </DateDropZone>
      <Divider />
      {(source[0] || source[1]) && (
        <Box
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            paddingBottom: '10px',
            minHeight: '4rem',
            marginTop: '0',
          }}
        >
          <InputLabel
            sx={{
              marginLeft: '0.5rem',
              marginTop: '0.5rem',
              fontSize: fontSize.medium,
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily:'Axiforma Black'
            }}
          >
            Time Difference In
          </InputLabel>
          <CanvasSelect<DifferenceUnitOption>
            value={selectedDifferenceUnit}
            options={differenceUnits}
            placeholder="Select"
            getLabel={(opt) => opt.label}
            getKey={(opt) => opt.value}
            onChange={(opt) => dispatch(setSource(propKey, activeFlow, 0, opt.value, 'text', 2))}
            disabled={isFlowUsedInOtherFlowsAsSource}
            style={{
              width: '95%',
              marginLeft: '0.5rem',
              marginTop: '0.5rem',
              height: '2rem',
              fontSize: '11px',
            }}
          />
        </Box>
      )}
    </Container>
  );
};
export default DateDifference;
